// UI of the standalone nesting tool (page "Anidado de DXF"). A drawing is read in the browser, repeated, laid out on
// the maker's sheets and offered as one DXF and one SVG per sheet. Nothing leaves the page. All user text reaches the
// DOM through .textContent only; the previews are SVG made of numbers.
import {
  describeDrawingError, detectKind, drawingToPieces, isDrawingError,
} from './wr-l3d-drawing.js?v=aaf4a0ca9e19';
import { numberFormat } from './wr-l3d-locale.js?v=aaf4a0ca9e19';
import { nestPieces } from './wr-l3d-nest.js?v=aaf4a0ca9e19';
import {
  nestFiles, nestItems, normalizeNest, summarizeNest,
} from './wr-l3d-nesttool.js?v=aaf4a0ca9e19';
import { createLanguage } from './wr-l3d-ui-i18n.js?v=aaf4a0ca9e19';
import { createZip } from './wr-l3d-zip.js?v=aaf4a0ca9e19';

const root = document.querySelector('[data-wr-l3d-nest]');
if (root) init(root);

function init(host) {
  const $ = (selector) => host.querySelector(selector);
  const $$ = (selector) => [...host.querySelectorAll(selector)];
  const els = {
    pick: $('[data-l3dn-pick]'), file: $('[data-l3dn-file]'), name: $('[data-l3dn-name]'), drop: $('[data-l3dn-drop]'),
    sizeButtons: $$('[data-l3dn-size]'), sizeHint: $('[data-l3dn-size-hint]'), heightField: $('[data-l3dn-height-field]'),
    height: $('[data-l3dn-height]'), copies: $('[data-l3dn-copies]'), sheetInputs: $$('[data-l3dn-sheet]'), presets: $$('[data-l3dn-preset]'),
    status: $('[data-l3dn-status]'), facts: $('[data-l3dn-facts]'), notes: $('[data-l3dn-notes]'), downloads: $('[data-l3dn-downloads]'),
    zip: $('[data-l3dn-zip]'), sheets: $('[data-l3dn-sheets]'), wa: $('[data-l3dn-wa]'),
  };
  const nf0 = numberFormat({ maximumFractionDigits: 0 });
  const nf1 = numberFormat({ maximumFractionDigits: 1 });
  const encoder = new TextEncoder();

  const state = { drawing: null, settings: normalizeNest(null) };
  let files = [];
  let timer = 0;

  try { state.settings = normalizeNest(JSON.parse(localStorage.getItem('wr-l3dn:settings') || 'null')); } catch { /* storage may be blocked */ }

  function track(name, params = {}) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...params });
      if (window.gtag) window.gtag('event', name, params);
    } catch { /* analytics must never break the tool */ }
  }

  function setStatus(message, isError = false) {
    els.status.textContent = message;
    els.status.classList.toggle('is-error', isError);
  }

  function syncForm() {
    const { sizeMode, heightMm, copies, sheet } = state.settings;
    for (const button of els.sizeButtons) button.setAttribute('aria-pressed', String(button.dataset.l3dnSize === sizeMode));
    els.height.value = String(heightMm);
    els.heightField.hidden = sizeMode === 'original' && state.drawing?.kind !== 'svg';
    els.copies.value = String(copies);
    for (const input of els.sheetInputs) input.value = String(sheet[input.dataset.l3dnSheet]);
    els.name.textContent = state.drawing ? `${state.drawing.name} (${Math.max(1, Math.round(state.drawing.text.length / 1024))} KB)` : 'Todavía no elegiste ninguno';
    els.name.classList.toggle('is-set', Boolean(state.drawing));
    els.wa.href = `https://wa.me/${(host.dataset.whatsapp || '').replace(/\D/g, '')}?text=${encodeURIComponent('Hola, quiero cotizar un corte con un archivo de anidado.')}`;
  }

  function persist() {
    try { localStorage.setItem('wr-l3dn:settings', JSON.stringify(state.settings)); } catch { /* storage may be blocked */ }
  }

  function download(name, data, type) {
    const url = URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  function renderSheets(layout) {
    els.sheets.replaceChildren(...layout.sheets.map((sheet) => {
      const li = document.createElement('li');
      li.className = 'wr-l3dn__sheet';
      const head = document.createElement('div');
      head.className = 'wr-l3dn__sheet-head';
      const title = document.createElement('strong');
      title.textContent = `Plancha ${sheet.index}`;
      const detail = document.createElement('span');
      detail.textContent = `${nf0.format(sheet.placements.length)} ${sheet.placements.length === 1 ? 'pieza' : 'piezas'} · ocupa ${nf0.format(sheet.extent.width)} × ${nf0.format(sheet.extent.height)} mm`;
      head.append(title, detail);
      const figure = document.createElement('div');
      const name = `Plancha_${String(sheet.index).padStart(2, '0')}`;
      const own = files.filter((file) => file.name.startsWith(name));
      const svg = own.find((file) => file.name.endsWith('.svg'));
      // the SVG is built from numbers only; its fixed millimetre size gives way to the width of the page
      if (svg) figure.innerHTML = svg.text.replace(/ width="[^"]*mm" height="[^"]*mm"/, ' class="wr-l3dn__sheet-svg" role="img" aria-label="Vista de la plancha"');
      const actions = document.createElement('div');
      actions.className = 'wr-l3dn__sheet-actions';
      for (const file of own) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'wr-l3d__chip-btn';
        button.textContent = file.name.endsWith('.dxf') ? 'DXF' : 'SVG';
        button.setAttribute('aria-label', `Descargar la plancha ${sheet.index} en ${button.textContent}`);
        button.addEventListener('click', () => {
          download(file.name, encoder.encode(file.text), file.name.endsWith('.dxf') ? 'application/dxf' : 'image/svg+xml');
          track('l3dn_download_sheet', { kind: button.textContent });
        });
        actions.append(button);
      }
      li.append(head, figure, actions);
      return li;
    }));
  }

  function update() {
    persist();
    syncForm();
    files = [];
    els.facts.hidden = true;
    els.downloads.hidden = true;
    els.notes.replaceChildren();
    els.sheets.replaceChildren();
    if (!state.drawing) { setStatus('Elegí un archivo para empezar.'); return; }
    const { sizeMode, heightMm, copies, sheet } = state.settings;
    const notes = [];
    let laid;
    try {
      const original = sizeMode === 'original' && state.drawing.kind === 'dxf';
      laid = drawingToPieces(state.drawing, { heightMm, original });
      if (sizeMode === 'original' && state.drawing.kind === 'svg') notes.push({ text: `Un SVG no trae medida real: lo escalamos a ${nf0.format(heightMm)} mm de alto.` });
    } catch (error) {
      setStatus(isDrawingError(error) ? describeDrawingError(error) : 'No pudimos procesar el dibujo. Probá con otro archivo.', true);
      return;
    }
    for (const warning of laid.warnings) notes.push({ text: warning.message });
    const items = nestItems(laid.pieces, copies);
    if (items.length < laid.pieces.length * copies) notes.push({ text: `Son demasiadas piezas: acomodamos las primeras ${nf0.format(items.length)}.` });
    const layout = nestPieces(items, sheet);
    const summary = summarizeNest(layout, items);
    files = nestFiles(layout);

    for (const size of summary.oversizeSizes) {
      notes.push({ text: `Una pieza de ${nf0.format(size.width)} × ${nf0.format(size.height)} mm no entra en tu plancha de ${nf0.format(layout.sheet.width)} × ${nf0.format(layout.sheet.height)} mm. Usá una plancha más grande o achicá el dibujo.` });
    }
    if (summary.sheets === 1) {
      const { extent } = layout.sheets[0];
      notes.push({ text: `Entra en 1 plancha y ocupa unos ${nf0.format(extent.width)} × ${nf0.format(extent.height)} mm: si te conviene, comprá un retazo.`, ok: true });
    } else if (summary.sheets > 1) {
      notes.push({ text: `Necesitás ${summary.sheets} planchas de ${nf0.format(layout.sheet.width)} × ${nf0.format(layout.sheet.height)} mm.`, ok: true });
    }
    if (summary.sheets === 0 && summary.oversize === 0) notes.push({ text: 'No hay piezas para acomodar.' });
    els.notes.replaceChildren(...notes.map((note) => {
      const li = document.createElement('li');
      li.className = `wr-l3d__note${note.ok ? ' wr-l3d__note--ok' : ''}`;
      li.textContent = note.text;
      return li;
    }));

    els.facts.hidden = false;
    const text = (name, value) => { host.querySelector(`[data-l3dn-fact="${name}"]`).textContent = value; };
    text('pieces', `${nf0.format(summary.placed)}${summary.oversize ? ` de ${nf0.format(items.length)}` : ''}`);
    text('sheets', nf0.format(summary.sheets));
    text('usage', `${nf1.format(summary.utilizationPercent)} %`);
    setStatus(summary.sheets > 0
      ? `Listo: ${nf0.format(summary.placed)} ${summary.placed === 1 ? 'pieza acomodada' : 'piezas acomodadas'} en ${nf0.format(summary.sheets)} ${summary.sheets === 1 ? 'plancha' : 'planchas'}.`
      : 'Ninguna pieza entra en la plancha.', summary.sheets === 0);
    els.downloads.hidden = summary.sheets === 0;
    renderSheets(layout);
  }

  function schedule(delay = 250) {
    clearTimeout(timer);
    timer = setTimeout(update, delay);
  }

  async function load(file) {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setStatus('Ese archivo pesa más de 8 MB. Simplificá el dibujo.', true); return; }
    if (!/\.(svg|dxf)$/i.test(file.name) && file.type !== 'image/svg+xml') { setStatus('Tiene que ser un archivo .dxf o .svg.', true); return; }
    setStatus('Leyendo el dibujo…');
    try {
      const text = await file.text();
      const kind = detectKind(file.name, text);
      if (!kind) { setStatus('Tiene que ser un archivo .dxf o .svg.', true); return; }
      const drawing = { name: file.name.slice(0, 120), text, kind };
      drawingToPieces(drawing, { heightMm: state.settings.heightMm, original: state.settings.sizeMode === 'original' && kind === 'dxf' }); // validate before replacing what is shown
      state.drawing = drawing;
      if (kind === 'dxf' && state.settings.sizeMode !== 'original') state.settings = { ...state.settings, sizeMode: 'original' }; // a CAD file is in real units: keep them by default
      track('l3dn_loaded', { kind, kb: Math.round(text.length / 1024) });
      update();
    } catch (error) {
      setStatus(isDrawingError(error) ? describeDrawingError(error) : 'No pudimos procesar el dibujo. Probá con otro archivo.', true);
    }
  }

  // ---- events -----------------------------------------------------------------------------------------
  els.pick.addEventListener('click', () => els.file.click());
  els.file.addEventListener('change', () => { const file = els.file.files?.[0]; els.file.value = ''; load(file); });
  for (const type of ['dragenter', 'dragover']) els.drop.addEventListener(type, (event) => { event.preventDefault(); els.drop.classList.add('is-over'); });
  for (const type of ['dragleave', 'drop']) els.drop.addEventListener(type, (event) => { event.preventDefault(); els.drop.classList.remove('is-over'); });
  els.drop.addEventListener('drop', (event) => load(event.dataTransfer?.files?.[0]));

  for (const button of els.sizeButtons) {
    button.addEventListener('click', () => { state.settings = normalizeNest({ ...state.settings, sizeMode: button.dataset.l3dnSize }); update(); });
  }
  els.height.addEventListener('input', () => { state.settings = normalizeNest({ ...state.settings, heightMm: els.height.value }); persist(); schedule(); });
  els.copies.addEventListener('input', () => { state.settings = normalizeNest({ ...state.settings, copies: els.copies.value }); persist(); schedule(); });
  for (const input of els.sheetInputs) {
    input.addEventListener('input', () => {
      state.settings = normalizeNest({ ...state.settings, sheet: { ...state.settings.sheet, [input.dataset.l3dnSheet]: Number.parseFloat(input.value) } });
      persist();
      schedule();
    });
  }
  for (const input of [els.height, els.copies, ...els.sheetInputs]) input.addEventListener('change', syncForm); // show the value that was really taken
  for (const button of els.presets) {
    button.addEventListener('click', () => {
      const [width, height] = button.dataset.l3dnPreset.split('x').map(Number);
      state.settings = normalizeNest({ ...state.settings, sheet: { ...state.settings.sheet, width, height } });
      update();
    });
  }
  els.zip.addEventListener('click', () => {
    if (files.length === 0) return;
    download('planchas.zip', createZip(files.map((file) => ({ name: file.name, data: encoder.encode(file.text) }))), 'application/zip');
    track('l3dn_download_zip', { files: files.length });
  });

  // another language: everything on screen is worked out again from the settings, so the numbers and messages follow
  const language = createLanguage(host, {
    onChange: update,
    onError: () => setStatus('No pudimos cargar ese idioma. Probá de nuevo.', true),
  });
  host.__l3dLang = language; // lets the browser tests read the same state a visitor sees

  syncForm();
  host.__l3dnState = () => ({ drawing: state.drawing?.name ?? null, settings: state.settings, files: files.map((file) => file.name) }); // lets the browser tests read what the page took
}
